// Core packages
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";

// Third party packages
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { DocumentArchiveDocsService } from "src/app/modules/properties/document-archive-docs.service";
import {
  dossierFilesCountMessage,
  dossierListErrorMessage,
  dossierPartErrorMessage,
  DossierPartRow,
  dossierPartRows,
  dossierPartSaveName,
  dossierPartSizeLabel,
  shouldReloadDossierParts,
} from "src/app/modules/properties/document-archive-dossier.utils";
import {
  skippedFilesCount,
  skippedFilesMessage,
  SKIPPED_FILES_HEADER,
  ZipDownloadMessage,
} from "src/app/modules/properties/document-files-zip.utils";
import { ConfigService } from "src/app/shared/services/config.service";

/**
 * What the button that opens the dialog passes in.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDossierDialogData {
  /** The property whose dossier is downloaded */
  propertyId: string;
}

/**
 * One part, as the dialog renders it: the row of the list with its two labels, computed once.
 *
 * @since 3.0.0
 */
export interface DossierPartView extends DossierPartRow {
  /** «156 file» */
  filesMessage: ZipDownloadMessage;
  /** «480,3 MB»; empty when the list gave no weight */
  sizeText: string;
}

/** Where the list of the parts stands */
type DossierLoadState = "loading" | "loaded" | "failed";

/**
 * The dossier zip of a property on the 3.0.0 archive, part by part (NEW-M17).
 *
 * It stands in for the legacy «Esporta file» dialog (`ExportCdDocsDialogComponent`) once the archive is
 * switched on. The legacy one asks `GET properties/:id/export-file`, which builds every zip of the other
 * archive into the public `/export` folder, and offers their public links. Here the dialog reads the
 * list of the parts (`GET /document-archive-docs/export/dossier`) and each «Scarica» asks for ONE part
 * with the session (`GET /export/dossier/:part`), saved from the blob: no path is ever public, and no
 * zip exists before it is asked for.
 *
 * One part at a time (decision of Luis, 27 October 2026): while a part downloads, the other buttons are
 * off. A part holds about 500 MB of files and arrives whole in the memory of the browser, and the list
 * and the parts share one per-address limit of 30 requests in 10 minutes.
 *
 * The list is shown whole or not at all (@see dossierPartRows). A 404 on a part reads the list again:
 * the back-end composes the dossier on every request, so a part can be gone between the list and the
 * download.
 *
 * The dialog can be closed while a part downloads, as «Scarica i documenti»: nothing is saved then.
 * ApiService does not cancel the request on unsubscribe, so the transfer still runs to its end before
 * the answer is dropped.
 *
 * Declared in DocumentArchiveDossierDialogModule, imported by PropertiesModule: it is in the initial
 * bundle with the pages that open it.
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-document-archive-dossier-dialog",
  templateUrl: "./document-archive-dossier-dialog.component.html",
  styleUrls: ["./document-archive-dossier-dialog.component.scss"],
})
export class DocumentArchiveDossierDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  /** Language of the session, for the decimal separator of the weights */
  lang = "it";

  state: DossierLoadState = "loading";

  parts: DossierPartView[] = [];

  /** Why the list could not be read; null unless `state` is "failed" */
  failure: ZipDownloadMessage | null = null;

  /** The part on its way, null when none: only one part downloads at a time */
  downloadingPart: string | null = null;

  /**
   * Class constructor
   */
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DocumentArchiveDossierDialogData,
    private dialogRef: MatDialogRef<DocumentArchiveDossierDialogComponent>,
    private documentArchiveDocsService: DocumentArchiveDocsService,
    private configService: ConfigService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private toastrService: ToastrService
  ) {}

  /**
   * Read the list of the parts.
   *
   * @since 3.0.0
   */
  ngOnInit(): void {
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;
    this.loadParts();
  }

  /**
   * Destroy component: a part on its way is no longer saved (@see DocumentArchiveDossierDialogComponent).
   *
   * @since 3.0.0
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Read the list again after a failure.
   *
   * @since 3.0.0
   */
  onRetry(): void {
    if (this.state !== "failed") {
      return;
    }
    this.loadParts();
  }

  /**
   * Download one part.
   *
   * The zip is saved under the name the back-end sends in `Content-Disposition`, and a warning tells how
   * many files it could not include, as «Scarica tutti» does. The dialog stays open: a dossier of
   * several parts is downloaded part after part.
   *
   * @since 3.0.0
   *
   * @param part The part whose «Scarica» was clicked
   */
  onDownload(part: DossierPartView): void {
    if (!part || this.state !== "loaded" || this.downloadingPart !== null) {
      return;
    }
    this.downloadingPart = part.part;

    this.subscriptions.push(
      this.documentArchiveDocsService
        .downloadDossierPart(this.propertyId, part.part)
        .pipe(finalize(() => (this.downloadingPart = null)))
        .subscribe(
          (res: HttpResponse<Blob> | Error) => {
            // ApiService emits a failure without status (network down, connection cut once the
            // zip had started) as a value, not as an error
            if (!(res instanceof HttpResponse) || !(res.body instanceof Blob)) {
              console.warn("Fascicolo (v3): part download failed", { status: 0 });
              this.showMessage(dossierPartErrorMessage(0, null, Date.now()), "error");
              return;
            }
            this.saveBlob(
              res.body,
              dossierPartSaveName(res.headers.get("Content-Disposition"), part.fileName)
            );

            const skipped = skippedFilesMessage(
              skippedFilesCount(res.headers.get(SKIPPED_FILES_HEADER))
            );
            if (skipped) {
              this.showMessage(skipped, "warning");
            }
          },
          (err: HttpErrorResponse) => {
            const status = err && typeof err.status === "number" ? err.status : 0;
            const retryAfter = err && err.headers ? err.headers.get("Retry-After") : null;
            console.warn("Fascicolo (v3): part download failed", { status });
            this.showMessage(dossierPartErrorMessage(status, retryAfter, Date.now()), "error");
            if (shouldReloadDossierParts(status)) {
              this.loadParts();
            }
          }
        )
    );
  }

  /**
   * Close the dialog.
   *
   * @since 3.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /** The property of the dialog, empty when the opener passed none */
  private get propertyId(): string {
    return this.data && typeof this.data.propertyId === "string" ? this.data.propertyId.trim() : "";
  }

  /**
   * Ask for the list of the parts and show it, or say why it could not be read.
   *
   * Without a property nothing is asked: there is no dossier to name.
   */
  private loadParts(): void {
    this.parts = [];
    this.failure = null;

    const propertyId = this.propertyId;
    if (!propertyId) {
      this.state = "failed";
      this.failure = dossierListErrorMessage(0, null, Date.now());
      return;
    }
    this.state = "loading";

    this.subscriptions.push(
      this.documentArchiveDocsService.dossierParts(propertyId).subscribe(
        (res) => {
          const rows = res && res.status ? dossierPartRows(res.data) : null;
          if (rows === null) {
            console.warn("Fascicolo (v3): parts list unreadable", { status: 200 });
            this.state = "failed";
            this.failure = dossierListErrorMessage(0, null, Date.now());
            return;
          }
          this.parts = rows.map((row) => ({
            ...row,
            filesMessage: dossierFilesCountMessage(row.files),
            sizeText: dossierPartSizeLabel(row.bytes, this.lang),
          }));
          this.state = "loaded";
        },
        (err: HttpErrorResponse) => {
          // The dialog says it in its body; a toast on top of it would only repeat it
          const status = err && typeof err.status === "number" ? err.status : 0;
          const retryAfter = err && err.headers ? err.headers.get("Retry-After") : null;
          console.warn("Fascicolo (v3): parts list failed", { status });
          this.state = "failed";
          this.failure = dossierListErrorMessage(status, retryAfter, Date.now());
        }
      )
    );
  }

  /**
   * Save a downloaded part. The blob is handed to FileSaver as a BLOB and not as a URL, which is what
   * keeps the library from probing the server with a synchronous HEAD request first.
   */
  private saveBlob(blob: Blob, fileName: string): void {
    FileSaver.saveAs(blob, fileName);
  }

  /**
   * Show a translated toast of a download
   */
  private showMessage(message: ZipDownloadMessage, level: "error" | "warning"): void {
    const text = this.translateService.instant(message.key, message.params);
    if (level === "warning") {
      this.toastrService.warning(text);
    } else {
      this.toastrService.error(text);
    }
  }
}

/**
 * Open the dossier dialog of a property on the 3.0.0 archive, with the size and the closing rule of the
 * legacy «Esporta file» dialog it stands in for: closed by «Chiudi» only.
 *
 * The four buttons that open the legacy dialog — «Scarica archivio fascicolo documentale» of the
 * property home and «Esporta file» of the property list, of the suspended list and of the dialog of the
 * statistics — call it instead once the archive is switched on (NEW-M17).
 *
 * @since 3.0.0
 *
 * @param dialog     MatDialog of the page that opens it
 * @param propertyId The property whose dossier is downloaded
 */
export function openDocumentArchiveDossierDialog(
  dialog: MatDialog,
  propertyId: string
): MatDialogRef<DocumentArchiveDossierDialogComponent> {
  const data: DocumentArchiveDossierDialogData = { propertyId };
  return dialog.open(DocumentArchiveDossierDialogComponent, {
    minWidth: "50%",
    maxHeight: "98vh",
    data,
    disableClose: true,
  });
}
